import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Group as GroupIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import GroupList from '../components/lists/GroupList';
import GroupForm from '../components/forms/GroupForm';
import GroupDetails from '../components/details/GroupDetails';
import Modal from '../components/common/Modal';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../components/common/ReadOnlyButton';

const Groups = ({
  users,
  groupMembers,
  fetchAllUsers,
  fetchGroupMembers,
  getGroup,
  createGroup,
  deleteGroup,
  assignUsersToGroup,
  removeUsersFromGroup,
}) => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoadingGroupDetails, setIsLoadingGroupDetails] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [groupMemberCounts, setGroupMemberCounts] = useState({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const searchTimeoutRef = useRef(null);
  const dataFetchedRef = useRef(false);
  const location = useLocation();
  const isReadOnly = useReadOnlyMode();

  // Fetch groups with pagination
  const fetchGroups = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await apiService.getAllGroups({
        page: pagination.page + 1, // API uses 1-indexed pages
        pageSize: pagination.pageSize,
        search: searchQuery || undefined,
      });
      if (response.success && response.data) {
        setGroups(response.data.groups || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [pagination.page, pagination.pageSize, searchQuery]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 400);
  };

  useEffect(() => {
    if (location.state) {
      if (location.state.showForm) {
        setShowForm(true);
      }
      // Handle navigation from permission set assignments page
      if (location.state.selectGroup) {
        handleGroupClick(location.state.selectGroup);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      if (users.length === 0) fetchAllUsers();
      dataFetchedRef.current = true;
    }
  }, [fetchAllUsers, users.length]);

  const fetchGroupMemberCounts = async () => {
    if (groups.length === 0 || isLoadingCounts) return;

    setIsLoadingCounts(true);
    const counts = {};

    try {
      const countPromises = groups.map(async (group) => {
        try {
          // Call API directly instead of fetchGroupMembers to avoid
          // overwriting the shared groupMembers state (race condition)
          const result = await apiService.getGroupMembers(group.name);
          const members = result?.success ? (result.data?.members || []) : [];
          counts[group.name] = members.length;
        } catch (error) {
          console.error(`Error fetching members for group ${group.name}:`, error);
          counts[group.name] = 0;
        }
      });

      await Promise.all(countPromises);
      setGroupMemberCounts(counts);
    } catch (error) {
      console.error('Error fetching group member counts:', error);
    } finally {
      setIsLoadingCounts(false);
    }
  };

  useEffect(() => {
    if (groups.length > 0) {
      fetchGroupMemberCounts();
    }
  }, [groups]);

  const handleRefresh = async () => {
    await fetchGroups();
    setGroupMemberCounts({});
  };

  const handleGroupClick = async (groupName) => {
    if (isLoadingGroupDetails) return;

    try {
      setIsLoadingGroupDetails(true);
      const groupData = await getGroup(groupName);
      if (groupData) {
        setSelectedGroup({
          ...groupData,
          assignments: [],
          applicationAssignments: [],
          isLoadingAssignments: true
        });

        try {
          const response = await apiService.getGroupAssignments(groupName);
          if (response.success && response.data) {
            setSelectedGroup(prev => prev ? ({
              ...prev,
              assignments: response.data.assignments || [],
              applicationAssignments: response.data.applicationAssignments || [],
              isLoadingAssignments: false
            }) : prev);
          } else {
            setSelectedGroup(prev => prev ? ({
              ...prev,
              isLoadingAssignments: false
            }) : prev);
          }
        } catch (error) {
          console.error('Error fetching group assignments:', error);
          setSelectedGroup(prev => prev ? ({
            ...prev,
            isLoadingAssignments: false
          }) : prev);
        }
      }
    } catch (error) {
      console.error('Error fetching group details:', error);
    } finally {
      setIsLoadingGroupDetails(false);
    }
  };

  const handleCreateGroup = async (groupData) => {
    const success = await createGroup(groupData);
    if (success) {
      setShowForm(false);
      fetchGroups(); // Refresh the list
    }
    return success;
  };

  const handleDeleteGroup = async (groupName) => {
    try {
      const success = await deleteGroup(groupName);
      if (success) {
        setSelectedGroup(null);
        fetchGroups();
      }
      return success;
    } catch (error) {
      console.error('Error in handleDeleteGroup:', error);
      return false;
    }
  };

  const groupsWithCounts = groups.map((group) => ({
    ...group,
    memberCount: groupMemberCounts[group.name] || 0,
  }));

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#D64794',
                width: 48,
                height: 48,
              }}
            >
              <GroupIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Groups
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {pagination.total} group{pagination.total !== 1 ? 's' : ''} in the system
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search groups..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: '#E5E5E5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D64794',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#D64794',
                  },
                },
              }}
            />
            <Tooltip title="Refresh Groups">
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    borderColor: '#D64794',
                    color: '#D64794',
                  },
                  '&:disabled': {
                    color: '#BDBDBD',
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                {isRefreshing ? (
                  <CircularProgress size={20} sx={{ color: '#D64794' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>

            <ReadOnlyButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
              disabled={isReadOnly}
              sx={{
                backgroundColor: '#D64794',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#B83A7D',
                },
              }}
            >
              Create Group
            </ReadOnlyButton>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Groups List */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
          minHeight: '400px',
        }}
      >
        <GroupList
          groups={groupsWithCounts}
          onGroupClick={handleGroupClick}
          isLoadingCounts={isLoadingCounts}
          loading={isRefreshing}
        />

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {showForm && (
          <Modal onClose={() => setShowForm(false)} key="group-form-modal">
            <GroupForm
              users={users}
              onSubmit={handleCreateGroup}
              onCancel={() => setShowForm(false)}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedGroup && (
          <Modal onClose={() => setSelectedGroup(null)} key="group-details-modal">
            <GroupDetails
              group={selectedGroup}
              users={users}
              groupMembers={groupMembers}
              onClose={() => setSelectedGroup(null)}
              onFetchMembers={fetchGroupMembers}
              onAddUsers={assignUsersToGroup}
              onRemoveUsers={removeUsersFromGroup}
              onDelete={handleDeleteGroup}
              onRevokeComplete={() => handleGroupClick(selectedGroup.name)}
            />
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Groups;
